import { getUserLocationPref } from '../services/geoService';

export type TimeFormatPreference = '12h' | '24h';

export const TIME_FORMAT_PREF_KEY = 'astro_time_format_pref';

/**
 * Get active timezone code or short label (e.g. 'IST', 'EST', 'GMT', 'UTC+5:30')
 */
export const getActiveTimezoneLabel = (): string => {
  try {
    const loc = getUserLocationPref();
    if (loc?.timezoneName) {
      // Extract code like 'IST' from 'IST (UTC+5:30)' or return timezoneName
      const match = loc.timezoneName.match(/^([A-Z\/]+)/);
      if (match) return match[1];
      return loc.timezoneName;
    }
    if (loc?.timezoneOffsetMinutes !== undefined) {
      const mins = loc.timezoneOffsetMinutes;
      const sign = mins >= 0 ? '+' : '-';
      const absMins = Math.abs(mins);
      const hrs = Math.floor(absMins / 60);
      const m = absMins % 60;
      return mins === 330 ? 'IST' : `UTC${sign}${hrs}${m > 0 ? `:${m.toString().padStart(2, '0')}` : ''}`;
    }
  } catch {}
  return 'IST';
};

/**
 * Get user configured time format preference ('12h' default or '24h')
 */
export const getTimeFormatPref = (): TimeFormatPreference => {
  try {
    const val = localStorage.getItem(TIME_FORMAT_PREF_KEY);
    if (val === '24h' || val === '12h') return val;
  } catch {}
  return '12h';
};

/**
 * Set and broadcast time format preference across the entire application
 */
export const setTimeFormatPref = (pref: TimeFormatPreference): void => {
  try {
    localStorage.setItem(TIME_FORMAT_PREF_KEY, pref);
    window.dispatchEvent(new CustomEvent('astro_time_format_changed', { detail: pref }));
  } catch {}
};

/**
 * Format any time string or Date according to the active global time format preference and active timezone
 * e.g. "07:15 PM IST" or "19:15 EST"
 */
export const formatTime = (
  timeInput?: string | Date | number | null,
  showSecondsIfPresent: boolean = true,
  overrideFormat?: TimeFormatPreference,
  tzOffsetMinutes?: number
): string => {
  if (timeInput === null || timeInput === undefined || timeInput === '') return 'N/A';

  const is24h = (overrideFormat || getTimeFormatPref()) === '24h';
  const tzLabel = getActiveTimezoneLabel();

  // Helper to append tzLabel if not already present
  const withTz = (timeStr: string) => {
    if (!tzLabel || timeStr.includes(tzLabel) || timeStr.includes('IST') || timeStr.includes('UTC')) {
      return timeStr;
    }
    return `${timeStr} ${tzLabel}`;
  };

  // If already a Date or numeric timestamp
  if (timeInput instanceof Date || typeof timeInput === 'number') {
    const d = typeof timeInput === 'number' ? new Date(timeInput) : timeInput;
    if (isNaN(d.getTime())) return 'N/A';

    const offsetMins = tzOffsetMinutes !== undefined ? tzOffsetMinutes : 330;
    const localMs = d.getTime() + offsetMins * 60 * 1000;
    const localDate = new Date(localMs);

    const hours24 = localDate.getUTCHours();
    const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');
    const seconds = String(localDate.getUTCSeconds()).padStart(2, '0');
    const secPart = showSecondsIfPresent ? `:${seconds}` : '';

    if (is24h) {
      return withTz(`${String(hours24).padStart(2, '0')}:${minutes}${secPart}`);
    } else {
      let hours12 = hours24 % 12;
      if (hours12 === 0) hours12 = 12;
      const ampm = hours24 >= 12 ? 'PM' : 'AM';
      return withTz(`${String(hours12).padStart(2, '0')}:${minutes}${secPart} ${ampm}`);
    }
  }

  const str = String(timeInput).trim();
  if (!str || str === 'N/A' || str === 'null' || str === 'undefined') return 'N/A';

  // If time string contains AM/PM like "07:15 PM" or "7:15:20 AM"
  const ampmMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)$/i);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10);
    const minutes = ampmMatch[2];
    const seconds = ampmMatch[3];
    const ampm = ampmMatch[4].toUpperCase();
    const secPart = (showSecondsIfPresent && seconds) ? `:${seconds}` : '';

    if (is24h) {
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      return withTz(`${String(hours).padStart(2, '0')}:${minutes}${secPart}`);
    } else {
      hours = hours % 12;
      if (hours === 0) hours = 12;
      return withTz(`${String(hours).padStart(2, '0')}:${minutes}${secPart} ${ampm}`);
    }
  }

  // If time string is in 24h format like "19:15" or "19:15:30" or "07:15"
  const time24Match = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (time24Match) {
    let hours24 = parseInt(time24Match[1], 10);
    const minutes = time24Match[2];
    const seconds = time24Match[3];
    const secPart = (showSecondsIfPresent && seconds) ? `:${seconds}` : '';

    if (is24h) {
      return withTz(`${String(hours24).padStart(2, '0')}:${minutes}${secPart}`);
    } else {
      const ampm = hours24 >= 12 ? 'PM' : 'AM';
      let hours12 = hours24 % 12;
      if (hours12 === 0) hours12 = 12;
      return withTz(`${String(hours12).padStart(2, '0')}:${minutes}${secPart} ${ampm}`);
    }
  }

  // Try parsing ISO date string
  if (str.includes('T')) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return formatTime(d, showSecondsIfPresent, overrideFormat);
    }
  }

  return str.includes(tzLabel) ? str : `${str} ${tzLabel}`;
};

const MONTHS_3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format any date into strict DD-Mmm-YYYY format (e.g. 05-Sep-2026, 05-Apr-1974)
 */
export const formatDate = (dateInput?: string | Date | number | null): string => {
  if (!dateInput) return 'N/A';
  
  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim();
    if (!trimmed || trimmed === 'N/A' || trimmed === 'null' || trimmed === 'undefined') return 'N/A';

    // If ISO string like 2026-09-05T12:34:56...
    if (trimmed.includes('T')) {
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        const day = String(parsed.getDate()).padStart(2, '0');
        const month = MONTHS_3[parsed.getMonth()];
        const year = parsed.getFullYear();
        return `${day}-${month}-${year}`;
      }
    }

    // Already in DD-Mon-YYYY (e.g. 05-Sep-2026, 5-sep-2026)
    const dMonYMatch = trimmed.match(/^(\d{1,2})[-/ ]([A-Za-z]{3,9})[-/ ](\d{4})$/);
    if (dMonYMatch) {
      const day = String(parseInt(dMonYMatch[1], 10)).padStart(2, '0');
      const monStr = dMonYMatch[2].slice(0, 3).toLowerCase();
      const monthIdx = MONTHS_3.findIndex(m => m.toLowerCase() === monStr);
      const monCap = monthIdx >= 0 ? MONTHS_3[monthIdx] : (monStr.charAt(0).toUpperCase() + monStr.slice(1));
      const year = dMonYMatch[3];
      return `${day}-${monCap}-${year}`;
    }

    // Check YYYY-MM-DD or YYYY/MM/DD or YYYY.MM.DD
    const ymdMatch = trimmed.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    if (ymdMatch) {
      const year = ymdMatch[1];
      const month = parseInt(ymdMatch[2], 10) - 1;
      const day = parseInt(ymdMatch[3], 10);
      if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
        return `${String(day).padStart(2, '0')}-${MONTHS_3[month]}-${year}`;
      }
    }

    // Check DD-MM-YYYY or DD/MM/YYYY or DD.MM.YYYY
    const dmyMatch = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
    if (dmyMatch) {
      const day = parseInt(dmyMatch[1], 10);
      const month = parseInt(dmyMatch[2], 10) - 1;
      const year = dmyMatch[3];
      if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
        return `${String(day).padStart(2, '0')}-${MONTHS_3[month]}-${year}`;
      }
    }
  }

  const d = typeof dateInput === 'number' || typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (d instanceof Date && !isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0');
    const month = MONTHS_3[d.getMonth()];
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  return String(dateInput);
};

/**
 * Format DOB strictly as DD-Mmm-YYYY (e.g. 05-Apr-1974)
 */
export const formatDOB = (dateStr?: string | Date | null): string => {
  return formatDate(dateStr);
};

/**
 * Format full date & time with DD-Mmm-YYYY and time (e.g. 05-Sep-2026, 07:15 AM or 05-Sep-2026, 19:15)
 */
export const formatDateTime = (dateInput?: string | Date | number | null): string => {
  if (!dateInput) return 'N/A';
  const d = typeof dateInput === 'number' || typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (d instanceof Date && !isNaN(d.getTime())) {
    const formattedD = formatDate(d);
    const formattedT = formatTime(d, false);
    return `${formattedD}, ${formattedT}`;
  }
  return formatDate(dateInput);
};

/**
 * Format any Date or timestamp such that if it is NOT today, it includes both the date (DD-Mmm) and time with timezone.
 * If it IS today, it displays only the time with timezone.
 */
export const formatSmartDateTime = (
  timeInput?: string | Date | number | null,
  showSecondsIfPresent: boolean = true,
  overrideFormat?: TimeFormatPreference
): string => {
  if (timeInput === null || timeInput === undefined || timeInput === '') return 'N/A';
  const d = typeof timeInput === 'number' || typeof timeInput === 'string' ? new Date(timeInput) : timeInput;
  if (!(d instanceof Date) || isNaN(d.getTime())) return formatTime(timeInput, showSecondsIfPresent, overrideFormat);

  const now = new Date();
  const isToday = 
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  const timeStr = formatTime(d, showSecondsIfPresent, overrideFormat);
  if (isToday) {
    return timeStr;
  }

  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS_3[d.getMonth()];
  return `${day}-${month}, ${timeStr}`;
};

/**
 * Format time string according to user's preference (e.g. 07:15 AM or 19:15)
 */
export const formatTimeToAMPM = (timeStr?: string, showSecondsIfPresent: boolean = true): string => {
  return formatTime(timeStr, showSecondsIfPresent);
};

/**
 * Calculate age string (e.g., "52y 5m" or "52 वर्ष 5 माह")
 */
export const calculateAgeString = (dateStr?: string, lang: string = 'en'): string => {
  if (!dateStr) return '';
  const birth = new Date(dateStr);
  if (isNaN(birth.getTime())) return '';
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return '';
  return lang === 'hi' ? `${years}व ${months}मा` : `${years}y ${months}m`;
};

/**
 * Format any number into correct English ordinal (1st, 2nd, 3rd, 4th, etc.)
 */
export const formatOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Pure, immutable date shifting by month offset with month-end and leap-year safety
 */
export function shiftMonthsImmutable(date: Date, months: number): Date {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const targetMonth = month + months;
  const targetYear = year + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;

  // Maximum days in target month
  const maxDays = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  const targetDay = Math.min(day, maxDays);

  return new Date(targetYear, normalizedMonth, targetDay, hours, minutes, seconds);
}


/**
 * Format a Date object with date and time in active timezone (e.g. "29 Sep 05:11 PM IST")
 */
export const formatDateTimeWithTz = (date: Date | string | number | null | undefined, tzOffsetMinutes?: number): string => {
  if (!date) return 'N/A';
  const d = typeof date === 'number' || typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return 'N/A';

  const offsetMins = tzOffsetMinutes !== undefined ? tzOffsetMinutes : 330;
  const localMs = d.getTime() + offsetMins * 60 * 1000;
  const localDate = new Date(localMs);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = localDate.getUTCDate();
  const mon = months[localDate.getUTCMonth()];
  const hours24 = localDate.getUTCHours();
  const minutes = String(localDate.getUTCMinutes()).padStart(2, '0');

  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  const ampm = hours24 >= 12 ? 'PM' : 'AM';

  const tzLabel = getActiveTimezoneLabel();
  return `${day} ${mon} ${String(hours12).padStart(2, '0')}:${minutes} ${ampm} ${tzLabel}`;
};
export function shiftYearsImmutable(date: Date, years: number): Date {
  return shiftMonthsImmutable(date, years * 12);
}

